import { Injectable } from "@angular/core";
import { IChatInboxPostMessage, IChatInboxMessagePayload, ChatInboxMessageTypes } from "../../models/integrations/chat-inbox.model";
import { Logger, LoggerService } from "../../../core/services/logger";
import { BehaviorSubject, Subject } from "rxjs";
import { AUDIO_TYPE, AudioService, AppVisibilityService, ConfigService, ElectronService } from "../../../core/services";
import { Router } from "@angular/router";
import { IPC_CHANNELS, NOTIFICATION_MODE } from "../../../../../electron-utils/electron.model";
import { BrowserNotificationService, NotificationService } from "../../../core/services/notifications";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
  })
export class ChatInboxService {
  private log: Logger;
  private _iframe: HTMLIFrameElement = document.createElement('iframe');

  private chatUrl: string;

  private notifications: IChatInboxPostMessage[] = [];
  private _notificationsCount = 0;

  public notifications$: BehaviorSubject<IChatInboxPostMessage[]> = new BehaviorSubject(this.notifications);
  public notificationsCount$: BehaviorSubject<number> = new BehaviorSubject(this._notificationsCount);

  public notificationAdded$: Subject<IChatInboxPostMessage> = new Subject();
  public notificationRemoved$: Subject<string> = new Subject();

  constructor(
    private loggerService: LoggerService,
    private configService: ConfigService,
    private audioService: AudioService,
    private router: Router,
    private electronService: ElectronService,
    private appVisibilityService: AppVisibilityService,
    private browserNotificationService: BrowserNotificationService,
    private translateService: TranslateService,
    private notificationService: NotificationService
  ){
    this.log = loggerService.getLoggerInstance('RomulusInbox')!;
  }

  public init(token: string, isVoxloud = true) {
    const chatMode: 'full' | 'inbox' = this.configService.config.settings?.chatMode ?? 'inbox';
    this.chatUrl = isVoxloud ? 'https://chat.voxloud.com' : 'https://chat.romulus.live';
    if (chatMode === 'full') {
      this._iframe.src = `${this.chatUrl}/api-login?token=${token}`;
    } else {
      this._iframe.src = `${this.chatUrl}/api-login?fullscreen=1&redirect_uri=/inbox-merged/chats&token=${token}`;
    }
    this._iframe.setAttribute('allow', 'microphone *; clipboard-write *');
    this._iframe.frameBorder = '0';
    this._iframe.style.position = 'absolute';
    this._iframe.hidden = true;
    document.body.appendChild(this._iframe);
    window.addEventListener('message', this.postMessageCallback);

    if(this.electronService.isElectron){
      this.electronService.ipcRenderer.on(IPC_CHANNELS.OPEN_INCOMING_MESSAGES_VIEW, () => {
        this.router.navigate(['home', 'inbox']);
      });
    }
    this._iframe.addEventListener('error', this.iframeErrorCallback);
  }

  public destroy(): void {
    this._iframe.removeEventListener('error', this.iframeErrorCallback);
    this._iframe.remove();
    window.removeEventListener('message', this.postMessageCallback);
  }

  public get iframe(): HTMLIFrameElement {
    return this._iframe;
  }

  public get notificationsCount(): number {
    return this._notificationsCount;
  }

  public removeNotificationAt(index: number) {
    const removed = this.notifications.splice(index, 1);
    this.notifications$.next(this.notifications);
    this.notificationRemoved$.next(removed[0].id);
  }

  public removeNotification(notification: IChatInboxPostMessage){
    const index = this.notifications.findIndex((n: IChatInboxPostMessage) => n.id === notification.id);
    if (index > -1) this.removeNotificationAt(index);
  }

  private iframeErrorCallback = () => {
    this.notificationService.error('inbox.frame-error');
  }

  private postMessageCallback = (message: MessageEvent) => {
    if(message.origin === this.chatUrl && message.data)
      this.processPostMessage(message.data);
    else
      this.log.warn('[ChatInbox] postMessage ignored — origin mismatch or no data');
  }

  private processPostMessage(message: IChatInboxPostMessage): void {
    message.id = `message_${Math.random().toString(16).slice(2)}`;
    const mode = this.configService.config?.settings?.notifications?.chat || NOTIFICATION_MODE.ALL;
    if (this.configService.config.settings?.doNotDisturb) {
      this.log.warn('[ChatInbox] doNotDisturb is ON — skipping notification');
    } else if (mode !== NOTIFICATION_MODE.DISABLED) {
      this.addNotification(message);
      this.audioService.playAndStop(AUDIO_TYPE.MESSAGE_RECEIVED);
      if (mode !== NOTIFICATION_MODE.INAPP) {
        this.openExternalNotification(message);
      }
    }
    if (this.router.url !== '/home/inbox') {
      this.addNotificationCount();
    }
  }

  private addNotification(message: IChatInboxPostMessage) {
    this.notifications.push(message);
    this.notifications$.next(this.notifications);
    this.notificationAdded$.next(message);
  }

  private clearAllNotifications(): void {
    this.notifications.forEach(n => this.notificationRemoved$.next(n.id));
    this.notifications = [];
    this.notifications$.next(this.notifications);
  }

  private addNotificationCount(): void {
    this._notificationsCount++;
    this.notificationsCount$.next(this._notificationsCount);
  }

  public clearNotificationCount(): void {
    this._notificationsCount = 0;
    this.notificationsCount$.next(this._notificationsCount);
    this.clearAllNotifications();
  }

  private openExternalNotification(message: IChatInboxPostMessage): void {
    if(this.electronService.isElectron){
      this.electronService.ipcRenderer.send(IPC_CHANNELS.OPEN_INCOMING_MESSAGE_WINDOW, message.id, JSON.stringify(message));
    } else if(!this.appVisibilityService.isVisible){
      this.openBrowserNotification(message);
    }
  }

  private openBrowserNotification(message: IChatInboxPostMessage): void {
    let title: string;
    let body: string;
    switch(message.type){
      case ChatInboxMessageTypes.MESSAGE: {
        const payload = message.payload as IChatInboxMessagePayload;
        title = this.translateService.instant('common.notification.incoming-message.new-message', {
          messagedBy: payload.data.messagedBy
        });
        body = payload.message;
        break;
      }

      case ChatInboxMessageTypes.LIVE_CHAT_REQUEST:
        title = this.translateService.instant('common.notification.incoming-message.live-chat.request');
        body = this.translateService.instant('common.notification.incoming-message.live-chat.info');
        break;

      case ChatInboxMessageTypes.LEAD:
        title = this.translateService.instant('common.notification.incoming-message.live-chat.request');
        body = this.translateService.instant('common.notification.incoming-message.live-chat.info');
        break;
    }

    const notification: Notification = this.browserNotificationService.notify(title, {
      body: body,
      badge: 'assets/icons/favicon.png',
      requireInteraction: true
    });

    const cleanup = this.appVisibilityService.onBecomeVisible(() => notification.close());

    notification.addEventListener('click', () => {
      window.focus();
      notification.close();
      cleanup();
    });
  }
}